import React, { useState, useEffect } from 'react';
import { trackPageView, trackButtonClick } from '../analytics';
import SEOMetaTags from '../components/SEOMetaTags';
import Header from '../components/Header';

const DiagnosePdf = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [runCheck, setRunCheck] = useState(true);
  const [showObjects, setShowObjects] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    trackPageView('/diagnose-pdf', 'Verify & Diagnose PDF Page');
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError('');
      setResult(null);
    } else {
      setError('Please select a valid PDF file');
      setSelectedFile(null);
    }
  };

  const handleDiagnose = async () => {
    if (!selectedFile) {
      setError('Please select a PDF file first');
      return;
    }

    setIsLoading(true);
    setError('');
    setResult(null);

    try {
      trackButtonClick('diagnose_pdf', 'diagnose_pdf');

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('run_check', runCheck);
      formData.append('show_objects', showObjects);

      const response = await fetch('https://api.temp2doc.com/organise/diagnose', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setResult(data);
      trackButtonClick('diagnose_success', 'diagnose_pdf');
    } catch (err) {
      trackButtonClick('diagnose_error', 'diagnose_pdf');
      setError(`Failed to verify & diagnose PDF: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEOMetaTags 
          title="Verify & Diagnose PDF - Validate & Check PDF Files Online Free | Temp2Doc"
          description="Validate and diagnose PDF files online for free with professional QPDF diagnostics. Check PDF integrity, detect errors and warnings, analyze PDF structure, verify compliance, and get detailed diagnostic reports. Identify corrupted PDFs and structural issues instantly."
          keywords="verify & diagnose PDF, validate PDF, check PDF, PDF validator, PDF diagnostics, PDF integrity check, QPDF check, PDF errors, PDF warnings, PDF verification, check PDF validity, PDF compliance check, PDF structure analysis, diagnose PDF problems, PDF file checker, corrupt PDF detection, PDF quality check"
          canonicalUrl="https://temp2doc.com/diagnose-pdf"
          structuredData={{
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Verify & Diagnose PDF",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "description": "Validate and diagnose PDF files online for free. Check PDF integrity, detect errors and warnings, analyze PDF structure with professional QPDF diagnostics. Get detailed diagnostic reports instantly.",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "890"
            },
            "featureList": [
              "Complete PDF validation and verification",
              "Error and warning detection",
              "PDF structure and integrity analysis",
              "QPDF professional diagnostics",
              "Detailed diagnostic reports",
              "Object and cross-reference information",
              "Corrupt PDF detection",
              "PDF compliance checking",
              "No file size limits up to 10MB",
              "Secure processing with automatic file deletion",
              "No registration required",
              "Free online tool"
            ]
          }}
        />
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-white to-blue-50 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <section className="max-w-6xl mx-auto text-center mb-12">
          <div className="inline-block p-3 bg-primary-100 rounded-full mb-4">
            <svg className="w-12 h-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Verify & Diagnose PDF - Validate & Check PDF Files
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Validate PDF files, detect errors and warnings, and analyze PDF structure with professional QPDF diagnostics.
          </p>
        </section>

        {/* Main Tool Section */}
        <section className="max-w-6xl mx-auto mb-12">
          <div className="bg-white rounded-xl shadow-lg p-8">
            {/* File Upload */}
            <div className="mb-6">
              <label htmlFor="pdf-upload" className="block text-sm font-medium text-gray-700 mb-2">
                Select PDF File
              </label>
              <div className="relative">
                <input
                  id="pdf-upload"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={isLoading}
                />
                <label
                  htmlFor="pdf-upload"
                  className={`flex items-center justify-center w-full px-4 py-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                    isLoading ? 'border-gray-300 bg-gray-50 cursor-not-allowed' : 'border-primary-300 hover:border-primary-500 bg-primary-50 hover:bg-primary-100'
                  }`}
                >
                  <div className="text-center">
                    <svg className="mx-auto h-12 w-12 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="mt-2 text-sm text-gray-600">
                      {selectedFile ? selectedFile.name : 'Click to upload or drag and drop'}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">PDF files up to 10MB</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Diagnostic Options */}
            <div className="space-y-4 mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Diagnostic Options</h3>
              
              <div className="space-y-3">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={runCheck}
                    onChange={(e) => setRunCheck(e.target.checked)}
                    className="mt-1 mr-3 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    disabled={isLoading}
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      Run Full QPDF Check (Recommended)
                    </span>
                    <p className="text-sm text-gray-500">
                      Performs complete validation and checks for errors, warnings, and structural issues.
                    </p>
                  </div>
                </label>

                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={showObjects}
                    onChange={(e) => setShowObjects(e.target.checked)}
                    className="mt-1 mr-3 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    disabled={isLoading}
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      Show Object & Cross-Reference Information
                    </span>
                    <p className="text-sm text-gray-500">
                      Include detailed object and cross-reference table information for advanced analysis.
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Diagnose Button */}
            <button
              onClick={handleDiagnose}
              disabled={!selectedFile || isLoading}
              className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                !selectedFile || isLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Diagnosing PDF...
                </span>
              ) : (
                'Verify & Diagnose PDF'
              )}
            </button>

            {/* Results Display */}
            {result && (
              <div className="mt-6 space-y-4">
                <div className={`p-4 rounded-lg border ${
                  result.valid 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center">
                    {result.valid ? (
                      <>
                        <svg className="w-6 h-6 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-lg font-semibold text-green-800">PDF is Valid</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-6 h-6 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-lg font-semibold text-red-800">PDF has Issues</span>
                      </>
                    )}
                  </div>
                </div>

                {result.errors && result.errors.length > 0 && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h4 className="text-sm font-semibold text-red-800 mb-2">Errors ({result.errors.length})</h4>
                    <ul className="space-y-1">
                      {result.errors.map((err, idx) => (
                        <li key={idx} className="text-sm text-red-700 pl-6">• {err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.warnings && result.warnings.length > 0 && (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h4 className="text-sm font-semibold text-yellow-800 mb-2">Warnings ({result.warnings.length})</h4>
                    <ul className="space-y-1">
                      {result.warnings.map((warn, idx) => (
                        <li key={idx} className="text-sm text-yellow-700 pl-6">• {warn}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.info && Object.keys(result.info).length > 0 && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="text-sm font-semibold text-blue-800 mb-2">Additional Information</h4>
                    <pre className="text-xs text-blue-700 overflow-x-auto bg-blue-100 p-3 rounded mt-2">
                      {JSON.stringify(result.info, null, 2)}
                    </pre>
                  </div>
                )}

                {result.valid && (!result.errors || result.errors.length === 0) && (!result.warnings || result.warnings.length === 0) && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700">
                      ✓ No errors or warnings found. Your PDF is in excellent condition!
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* How It Works */}
        <section className="max-w-6xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-full mb-4 mx-auto font-bold text-lg">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">Upload Your PDF</h3>
              <p className="text-gray-600 text-center">
                Select the PDF file you want to validate and diagnose. Files up to 10MB are supported.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-full mb-4 mx-auto font-bold text-lg">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">Run Diagnostics</h3>
              <p className="text-gray-600 text-center">
                Enable full QPDF check and optionally include object information for detailed analysis.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-full mb-4 mx-auto font-bold text-lg">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">View Results</h3>
              <p className="text-gray-600 text-center">
                Get instant verify and diagnostic results with errors, warnings, and detailed information about your PDF.
              </p>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="max-w-6xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">Key Features</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Complete Validation</h3>
              <p className="text-gray-600 text-sm">
                Professional QPDF diagnostics to ensure your PDF meets all standards.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Detection</h3>
              <p className="text-gray-600 text-sm">
                Identify critical errors that could prevent your PDF from working correctly.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Warning Analysis</h3>
              <p className="text-gray-600 text-sm">
                Get notified about potential issues that might affect PDF compatibility.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Structure Analysis</h3>
              <p className="text-gray-600 text-sm">
                Detailed object and cross-reference information for advanced analysis.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Secure & Private</h3>
              <p className="text-gray-600 text-sm">
                Your files are processed securely and automatically deleted after diagnosis.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Instant Results</h3>
              <p className="text-gray-600 text-sm">
                Get comprehensive diagnostic results in seconds with clear information.
              </p>
            </div>
          </div>
        </section>

        {/* Related Tools Section */}
        <section className="max-w-6xl mx-auto">
          <nav className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Related PDF Tools</h2>
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <li>
              <a href="/chat-with-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Chat PDF With AI</span>
              </a>
            </li>
            <li>
              <a href="/merge-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Merge PDF</span>
              </a>
            </li>
            <li>
              <a href="/split-remove-rotate-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Split Remove Rotate PDF</span>
              </a>
            </li>
            <li>
              <a href="/pdf-to-word" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">PDF to Word</span>
              </a>
            </li>
            <li>
              <a href="/pdf-to-excel" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">PDF to Excel</span>
              </a>
            </li>
            <li>
              <a href="/pdf-to-image" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">PDF to Image</span>
              </a>
            </li>
            <li>
              <a href="/pdf-to-powerpoint" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">PDF to PowerPoint</span>
              </a>
            </li>
            <li>
              <a href="/protect-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Protect PDF</span>
              </a>
            </li>
            <li>
              <a href="/unlock-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Unlock PDF</span>
              </a>
            </li>
            <li>
              <a href="/transform-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Repair & Transform PDF</span>
              </a>
            </li>
            <li>
              <a href="/compress-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Compress PDF</span>
              </a>
            </li>
            <li>
              <a href="/" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Back to Home</span>
              </a>
            </li>
          </ul>
          </nav>
        </section>
      </main>
    </>
  );
};

export default DiagnosePdf;
